export const vetnestCaseStudy = {
  title: 'VetNest: Veterinary Website Development Case Study | Suriya',
  description:
    'Inside VetNest: a custom veterinary website, service catalogue and permission-aware dashboard. Explore the design, architecture and implementation approach.',
  url: 'https://www.suriyaprakash.in/work/vetnest',
  liveUrl: 'https://vetnest.suriyaprakash.in',
  image: 'https://www.suriyaprakash.in/images/work/vetnest/homepage.jpg',
  sections: [
    ['brief', 'The brief'],
    ['experience', 'The experience'],
    ['architecture', 'The architecture'],
    ['decisions', 'Key decisions'],
    ['validation', 'Engineering quality'],
    ['fit', 'Your project'],
    ['questions', 'Questions'],
  ],
  journeys: [
    {
      number: '01',
      audience: 'For pet parents',
      title: 'Understand the care before making contact.',
      body: 'I designed the public journey to help a pet parent understand the care before making contact. I use approachable language, clear service discovery and practical visit information, then separate short introductions from full descriptions, durations and current prices. I gave team profiles room for biographies and professional information so the people behind the care have a meaningful presence. Every page supports the next step without asking visitors to understand the clinic’s internal structure.',
      detail: 'Public website → service detail → clinic enquiry',
      image: 'services',
      alt: 'VetNest services page with service descriptions, consultation durations and sample prices',
    },
    {
      number: '02',
      audience: 'For the clinic team',
      title: 'Publish once. Keep the story consistent.',
      body: 'I built publishing workflows for services, product categories, banners and staff profiles. I combine structured fields with rich-text descriptions and owned photo galleries, and I make publication an explicit decision. I also connect public staff visibility to the linked account’s active state. I keep clinic identity and opening hours separate from private configuration, giving editors a focused way to maintain public information while preserving a clear data boundary.',
      detail: 'Draft content → review details → explicit publication',
      image: 'team',
      alt: 'VetNest team directory displaying fictional staff profiles and clearly illustrative veterinary care photographs',
    },
    {
      number: '03',
      audience: 'For daily operations',
      title: 'Connect the records behind the visit.',
      body: 'I connected customers, pets, services and staff-assigned appointments in the dashboard. I derive appointment duration from the selected service, offer only the start times that fit opening hours and a staff member’s existing bookings, and support repeat bookings, attendance and no-show tracking. I issue invoices from the price and tax the visit was booked at and record payments append-only, send reminders before visits and give each staff member a private calendar feed. I built a separate customer portal for the customer’s own profile and active pet records, with its own authentication boundary. That gives me one connected data model with deliberately different experiences for the people using it.',
      detail: 'Customer → pet → service → free slot → visit → invoice',
      image: null,
      alt: '',
    },
  ],
  decisions: [
    {
      number: '01',
      title: 'Authorization belongs on the server.',
      tradeoff: 'Not just a hidden button.',
      body: 'I derive identity from server-side sessions and use Xonava’s attribute-based access control for module admission, row scope and field projection. I keep supporting dropdown reads narrow and module-owned. That lets me make permissions consistent across lists, details and mutations, while using the interface to communicate the actions available to each person.',
      outcome:
        'A consistent permission boundary across lists, details and writes.',
    },
    {
      number: '02',
      title: 'A write is more than an UPDATE.',
      tradeoff: 'Correctness before convenience.',
      body: 'I use the framework’s write lifecycle to connect version checks, before-and-after authorization, idempotency, audit events and cache invalidation. I add application rules such as staff scheduling and media ownership at that boundary, keeping correctness-critical effects together. I chose this approach to make the consistency rules explicit and reusable across feature modules.',
      outcome:
        'Optimistic concurrency and atomic write-side effects, not last-write-wins editing.',
    },
    {
      number: '03',
      title: 'Published media needs an owner.',
      tradeoff: 'Uploading is not publishing.',
      body: 'I attach photos to their parent records with explicit references and make fresh public serving follow the owner’s visibility. I use a separate aggregate for public staff presentation, keeping login data out of the profile model. I also account for the documented cache lifetime when designing publication changes, so the ownership and delivery rules remain clear.',
      outcome:
        'A reviewed relationship between file storage, content ownership and visibility.',
    },
    {
      number: '04',
      title: 'Useful content needs structure.',
      tradeoff: 'More than a one-line description.',
      body: 'I use short summaries for cards and introductions, and full rich-text documents for biographies, preparation guidance and catalogue details. I align validation with the renderer’s supported content model. I populated the demo with substantial descriptions in those actual fields so I could exercise long pages, editorial states and the content experience as a whole.',
      outcome:
        'Twenty-nine distinct seeded detail pages across staff, categories and offerings.',
    },
    {
      number: '05',
      title: 'Time and money must stay true.',
      tradeoff: 'A booking is a record, not a guess.',
      body: 'I store appointment times as UTC instants and convert from the clinic’s own time zone, so opening hours and daylight changes stay correct. I copy the service price and tax onto the visit when it is booked, so a later price change never rewrites history. Invoice numbers come from a sequence asserted at commit time, and payments are append-only: a mistake is corrected with an opposite entry, never an edit.',
      outcome:
        'Schedules, invoices and payments that can be audited after the fact.',
    },
  ],
  delivery: [
    [
      'Discover',
      'I map your clinic’s audience, existing tools, content ownership and operational needs. I connect what the public needs to understand with what your team needs to manage.',
    ],
    [
      'Adapt',
      'I shape the visual identity, service hierarchy, content model and permissions around that brief, adapting existing foundations and designing new workflows where needed.',
    ],
    [
      'Validate',
      'I review content and access rules, test the agreed workflows and configure the delivery environment. I plan data import, recovery and acceptance checks alongside the build.',
    ],
    [
      'Launch & improve',
      'I release against the agreed acceptance checks, then use enquiries, content feedback and field performance to guide the next improvements.',
    ],
  ],
  questions: [
    [
      'Is VetNest a real veterinary clinic?',
      'I built VetNest as an independent software project. The demo uses a fictional clinic and sample records to make the connected workflows easy to explore. Care photographs illustrate the interface rather than identifying the named demo staff.',
    ],
    [
      'Can this become a website for my veterinary practice?',
      'Yes. I can adapt the branding, content structure, service catalogue, team publishing and operational workflows around your practice. I start with your requirements, then agree the implementation, integrations, hosting and support that suit the project.',
    ],
    [
      'How did I build the appointment workflow?',
      'I built staff-managed appointments connected to customers, pets, services and staff schedules. I derive the visit length from the service and search for free start times within opening hours that avoid a staff member’s existing bookings, then record attendance and send a single reminder per visit. Public pages provide service information and enquiry paths, while the staff team manages appointment creation.',
    ],
    [
      'What did I bring together in VetNest?',
      'I connected a public veterinary website with service and team publishing, customer and pet records, a separate customer portal and staff-managed appointments. I use a permission-aware foundation so those related workflows can serve different people through clear access boundaries.',
    ],
    [
      'How does billing work?',
      'I issue an invoice from the price and tax the visit was booked with, number it from a sequence checked at commit time and record payments by method as append-only entries. Corrections are made with an opposite entry, so the payment history stays complete.',
    ],
    [
      'Why is the demo excluded from search results?',
      'I keep the product story discoverable here and use noindex for the fictional clinic demo. For a real clinic implementation, I configure its own indexing policy, verified business details, canonical origin and search setup around the actual practice.',
    ],
    [
      'Can I see the application or discuss a project?',
      'Yes. The public VetNest demo is available at vetnest.suriyaprakash.in. It uses a fictional clinic and sample records, while demonstrating the real interface and connected workflows. You can also email me to discuss how I would adapt the platform for your practice.',
    ],
  ],
} as const

export function vetnestStructuredData(authorName: string) {
  const { url, liveUrl, title, description, image } = vetnestCaseStudy
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#page`,
        url,
        name: title,
        description,
        inLanguage: 'en-IN',
        isPartOf: { '@id': 'https://www.suriyaprakash.in/#website' },
        mainEntity: { '@id': `${url}#article` },
        breadcrumb: { '@id': `${url}#breadcrumbs` },
      },
      {
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: 'VetNest — a veterinary website and clinic workflow platform',
        description,
        image: [image],
        mainEntityOfPage: { '@id': `${url}#page` },
        inLanguage: 'en-IN',
        author: {
          '@type': 'Person',
          '@id': 'https://www.suriyaprakash.in/#person',
          name: authorName,
          url: 'https://www.suriyaprakash.in',
        },
        about: [
          'Veterinary website development',
          'Clinic workflow software',
          'Attribute-based access control',
        ],
        articleSection: 'Software development case study',
        mentions: { '@id': `${liveUrl}/#application` },
      },
      {
        '@type': 'WebApplication',
        '@id': `${liveUrl}/#application`,
        url: liveUrl,
        name: 'VetNest interactive demo',
        description:
          'A published veterinary website and clinic workflow demonstration using a fictional clinic and sample records.',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        isAccessibleForFree: true,
        creator: {
          '@type': 'Person',
          '@id': 'https://www.suriyaprakash.in/#person',
          name: authorName,
        },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumbs`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Portfolio',
            item: 'https://www.suriyaprakash.in/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Case studies',
            item: 'https://www.suriyaprakash.in/work',
          },
          { '@type': 'ListItem', position: 3, name: 'VetNest', item: url },
        ],
      },
    ],
  }
}
